import {
  UpdatePatientListConfigurationDto,
  QueryFactory,
} from '@lmt/shared-types';

import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { PatientListConfigurationData } from '@lmt/unicorn-shared/app/components/patients/list/common/types';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import styles from './SaveButton.module.scss';
import { useEventCallback } from '@mui/material';
import { useModal } from '../../../common/ui-kit/modal/useModal';
import { useTranslation } from 'react-i18next';

interface Props {
  configurationId: number;
  values: PatientListConfigurationData;
  onNewConfiguration: (configurationId: number) => void;
}

export const SaveButton: React.FC<Props> = (props) => {
  const i18n = useScopedTranslation('patients.list.save_configuration');
  const i18nGlobal = useTranslation();
  const forms = useGlobalForms();
  const queryClient = useQueryClient();
  const claims = useClaims();
  const { configurationId, values, onNewConfiguration } = props;

  const saveData = useEventCallback(
    async (data: UpdatePatientListConfigurationDto) => {
      try {
        await QueryFactory.PatientListConfigurationClient.editPatientListConfiguration(
          configurationId,
          data,
        );
        await queryClient.invalidateQueries({
          queryKey:
            QueryFactory.PatientListConfigurationQuery.getPatientListConfigurationQueryKey(
              configurationId,
            ),
        });
        await queryClient.invalidateQueries({
          queryKey:
            QueryFactory.PatientListConfigurationQuery.getPatientListConfigurationsQueryKey(),
        });
        toast(i18n.t('toast'), { type: 'success' });
      } catch (e) {
        const error = convertToErrorString(e).replace('isShared: ', '');
        await modals.showError({
          text: error,
        });
      }
    },
  );
  const modals = useModal();
  const onClick = async () => {
    const result = await modals.showMultiButton({
      title: i18n.t('dialog.title'),
      text: i18n.t('dialog.description', {
        configName: i18nGlobal.t(values.name as any),
      }),
      buttons: [
        {
          id: 'save_current',
          text: i18n.t('dialog.save_current'),
          color: ButtonColor.Primary,
        },
        {
          id: 'save_new',
          text: i18n.t('dialog.save_new'),
          color: ButtonColor.Default,
        },
      ],
    });
    if (result === null) return;
    if (result === 'save_new') {
      forms.openPatientListConfigurationCreating(
        {
          ...values,
          isShared: claims.permissions.manageSharedPatientListConfigurations
            ? values.isShared
            : false,
          name: undefined,
        },
        onNewConfiguration,
      );
    } else if (result === 'save_current') {
      await saveData(values);
    }
  };

  return (
    <div onClick={onClick} className={styles.button}>
      {i18n.t('save_button')}
    </div>
  );
};
